import api from '../axios.config';

/**
 * Whether a person is here, and how this organisation wants routing to work.
 *
 * Both live behind the same idea: a conversation that needs a human needs to
 * know who is around to take it. Neither has anything to do with lead
 * OWNERSHIP, which is the standing relationship on the contact and a different
 * question with a different lifetime.
 */

export type AvailabilityStatus = 'AVAILABLE' | 'BUSY' | 'AWAY' | 'OFFLINE';

export interface Availability {
  userId: string;
  status: AvailabilityStatus;
  maxConcurrent: number;
  /** How many live conversations this person is holding right now. */
  holding: number;
  /** Available AND under capacity — the two fail differently. */
  hasCapacity: boolean;
  statusChangedAt: string;
  lastSeenAt: string;
}

export interface RoutingConfig {
  organizationId: string;
  enabled: boolean;
  claimTtlSeconds: number;
  botResumePolicy: 'UNTIL_RELEASED' | 'UNTIL_TIMER';
  sessionCreatesOwnership: boolean;
  requeueOnDisconnect: boolean;
  respectAvailabilityForOwnership: boolean;
}

/**
 * What to show for a status.
 *
 * The wording is about the person, not the system: somebody reads "Away" and
 * knows what it means about them, where "INACTIVE" reads as a record state.
 */
export const STATUS_META: Record<AvailabilityStatus, { label: string; dot: string; help: string }> = {
  AVAILABLE: {
    label: 'Available',
    dot: 'bg-emerald-500',
    help: 'New conversations can be handed to you.',
  },
  BUSY: {
    label: 'Busy',
    dot: 'bg-amber-500',
    help: 'You keep what you have; nothing new arrives.',
  },
  AWAY: {
    label: 'Away',
    dot: 'bg-gray-400',
    help: 'Signed in, but stepped away from the desk.',
  },
  OFFLINE: {
    label: 'Offline',
    dot: 'bg-gray-300 dark:bg-gray-600',
    help: 'Not working. Nothing will be routed to you.',
  },
};

export const STATUS_ORDER: AvailabilityStatus[] = ['AVAILABLE', 'BUSY', 'AWAY', 'OFFLINE'];

const unwrap = <T,>(res: any): T | null =>
  res?.data?.success && res.data.data !== undefined ? (res.data.data as T) : null;

export const availabilityService = {
  /** My own row. Created as OFFLINE the first time it is asked for. */
  async mine(): Promise<Availability | null> {
    try {
      return unwrap<Availability>(await api.get('/v1/conversations/availability/me'));
    } catch {
      // A failure here must never break the sidebar it renders in. The control
      // simply does not appear, which is also what happens for an organisation
      // that has not turned routing on.
      return null;
    }
  },

  /**
   * Set one or both. Sending only what changed is deliberate: changing your
   * capacity should not quietly sign you in, and going available should not
   * reset a capacity you set this morning.
   */
  async setMine(patch: { status?: AvailabilityStatus; maxConcurrent?: number }): Promise<
    { ok: true; data: Availability } | { ok: false; error: string }
  > {
    try {
      const res = await api.put('/v1/conversations/availability/me', patch);
      const data = unwrap<Availability>(res);
      return data
        ? { ok: true, data }
        : { ok: false, error: res?.data?.message || 'Could not update your status.' };
    } catch (e: any) {
      return {
        ok: false,
        error: e?.response?.data?.message || 'Could not update your status.',
      };
    }
  },

  /** Everyone in the organisation — the roster a supervisor reads. */
  async roster(): Promise<Availability[]> {
    try {
      return unwrap<Availability[]>(await api.get('/v1/conversations/availability')) ?? [];
    } catch {
      return [];
    }
  },

  /**
   * This organisation's routing policy.
   *
   * An organisation with no row reads as disabled, and asking does not create
   * one — so this is the check a screen makes before deciding to exist.
   */
  async routingConfig(): Promise<RoutingConfig | null> {
    try {
      return unwrap<RoutingConfig>(await api.get('/v1/conversations/routing-config'));
    } catch {
      return null;
    }
  },

  async saveRoutingConfig(patch: Partial<RoutingConfig>): Promise<
    { ok: true; data: RoutingConfig } | { ok: false; error: string }
  > {
    try {
      const res = await api.put('/v1/conversations/routing-config', patch);
      const data = unwrap<RoutingConfig>(res);
      return data
        ? { ok: true, data }
        : { ok: false, error: res?.data?.message || 'Could not save.' };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.message || 'Could not save.' };
    }
  },
};
